import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorCode } from '@common/http/enums/error-code.enum';
import { BranchesService } from '@portal-core/branches/services/branches.service';
import { ChecklistTemplate } from '@portal-core/checklist-templates/models/checklist-template.model';
import { ChecklistTemplatesService } from '@portal-core/checklist-templates/services/checklist-templates.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { FormsService } from '@portal-core/forms/services/forms.service';
import { ProjectChecklistType } from '@portal-core/project-checklists/enums/project-checklist-type.enum';
import { ProjectChecklist } from '@portal-core/project-checklists/models/project-checklist.model';
import { ProjectChecklistsService } from '@portal-core/project-checklists/services/project-checklists/project-checklists.service';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { catchError, first, map, Observable, of, Subscription } from 'rxjs';

export interface ProjectChecklistSettingsForm {
  name: FormControl<string>;
  description: FormControl<string>;
  type: FormControl<ProjectChecklistType>;
  branchId: FormControl<string[]>;
  template: FormControl<ChecklistTemplate>;
}

@Component({
  selector: 'mc-project-checklist-settings-form',
  templateUrl: './project-checklist-settings-form.component.html',
  styleUrls: ['./project-checklist-settings-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class ProjectChecklistSettingsFormComponent implements OnInit, OnChanges {
  @Input() branchName?: string;
  @Input() checklist?: ProjectChecklist;
  @Input() licenseId: number;
  @Input() projectId?: number;
  @Input() mode: 'create-checklist' | 'edit-checklist' | 'create-template' | 'edit-template';
  @Input() showFormFooter?: boolean = true;
  @Input() showTemplateField?: boolean = false;
  @Input() template?: ChecklistTemplate;

  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('nameInput', { static: false }) nameInput!: ElementRef<HTMLInputElement>;

  @InputObservable('projectId') projectId$: Observable<number>;

  ProjectChecklistType: typeof ProjectChecklistType = ProjectChecklistType;

  checklistSettingsForm: FormGroup<ProjectChecklistSettingsForm>;
  checklistTypeSubscription: Subscription;
  savingState: LoadingState<string> = new LoadingState<string>();
  templates$: Observable<ChecklistTemplate[]>;

  public get valid(): boolean {
    return this.checklistSettingsForm?.valid;
  }

  public get dirty(): boolean {
    return this.checklistSettingsForm?.dirty;
  }

  constructor(
    private fb: FormBuilder,
    private errorService: ErrorService,
    private formsService: FormsService,
    private projectChecklistsService: ProjectChecklistsService,
    private checklistTemplatesService: ChecklistTemplatesService,
    private branchesService: BranchesService,
    private snackBar: MatSnackBar
  ) {
    this.buildForm();
  }

  ngOnInit() {
    this.templates$ = this.checklistTemplatesService.getChecklistTemplatesByLicenseId$(this.licenseId);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.branchName || changes.checklist || changes.mode || changes.projectId || changes.template) {
      const template = this.checklistSettingsForm?.get('template')?.value;
      this.resetForm();

      if (changes.checklist && template) {
        this.checklistSettingsForm?.get('template')?.patchValue(template, { emitEvent: false });
      }
    }
  }

  focus() {
    this.nameInput?.nativeElement?.focus();
  }

  onCancelClicked() {
    this.cancel.emit();
  }

  onSubmit() {
    if (!this.checklistSettingsForm.valid) {
      return;
    }

    if (this.mode === 'create-checklist' || this.mode === 'edit-checklist') {
      this.projectChecklistSubmit();
    } else if (this.mode === 'create-template' || this.mode === 'edit-template') {
      this.checklistTemplateSubmit();
    }
  }

  private projectChecklistSubmit() {
    const checklist: ProjectChecklist = {
      ...this.checklist,
      Name: this.checklistSettingsForm.get('name')?.value,
      Type: this.checklistSettingsForm.get('type').value,
      Description: this.checklistSettingsForm.get('description').value,
      BranchName: this.branchesService.getBranchNameFromId(this.checklistSettingsForm.get('branchId').value[0])
    };

    this.savingState.update(true);
    this.projectChecklistsService.updateProjectChecklist$(checklist).subscribe(() => {
      this.savingState.update(false);
      this.checklistSettingsForm.markAsPristine();
      this.snackBar.open('The checklist has been updated.', 'OK', { duration: 2500 });
      this.saved.emit();
    }, error => {
      this.savingState.update(false, 'Unable to update the checklist', this.errorService.getErrorMessages(error));
    });
  }

  private checklistTemplateSubmit() {
    const template: ChecklistTemplate = {
      ...this.template,
      Name: this.checklistSettingsForm.get('name').value,
      Type: this.checklistSettingsForm.get('type').value,
      Description: this.checklistSettingsForm.get('description').value
    };

    this.savingState.update(true);
    this.checklistTemplatesService.updateChecklistTemplate$(template).pipe(first()).subscribe(() => {
      this.savingState.update(false);
      this.checklistSettingsForm.markAsPristine();
      this.snackBar.open('The checklist template has been updated.', 'OK', { duration: 2500 });
      this.saved.emit();
    }, error => {
      this.savingState.update(false, 'Unable to update the checklist template', this.errorService.getErrorMessages(error));
    });
  }

  private buildForm() {
    this.checklistSettingsForm = this.fb.group<ProjectChecklistSettingsForm>({
      name: new FormControl(null, { asyncValidators: [this.createNameAsyncValidator()], validators: [Validators.required, Validators.maxLength(40)] }),
      description: new FormControl(null, Validators.maxLength(250)),
      type: new FormControl(null),
      branchId: new FormControl(null, Validators.required),
      template: new FormControl(null)
    });

    // Listen to the checklist type changing to enable/disable the branch selector
    this.checklistTypeSubscription = this.checklistSettingsForm.get('type').valueChanges.subscribe(type => {
      if (this.mode === 'create-template' || this.mode === 'edit-template' || type === ProjectChecklistType.GenericList) {
        this.checklistSettingsForm.get('branchId').disable();
      } else {
        this.checklistSettingsForm.get('branchId').enable();
      }
    });
  }

  private resetForm() {
    this.checklistSettingsForm.reset({
      name: this.checklist?.Name ?? this.template?.Name ?? '',
      description: this.checklist?.Description ?? this.template?.Description ?? '',
      type: this.checklist?.Type ?? this.template?.Type ?? ProjectChecklistType.ProjectFiles,
      branchId: this.checklist?.BranchName ? [this.branchesService.buildBranchId(this.checklist.ProjectId, this.checklist.BranchName)] : [this.branchesService.buildBranchId(this.projectId, this.branchName)],
      template: null
    });

    // The type cannot be changed when editing an existing checklist
    if (this.mode === 'edit-checklist') {
      this.checklistSettingsForm.get('type').disable();
    } else {
      this.checklistSettingsForm.get('type').enable();
    }
  }

  private createNameAsyncValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      if (control.value) {
        if ((this.mode === 'create-checklist' || this.mode === 'edit-checklist') && typeof this.projectId === 'number') {
          // If the name hasn't changed then its valid
          if (this.mode === 'edit-checklist' && control.value === this.checklist?.Name) {
            return of(null);
          } else {
            return this.projectChecklistsService.verifyChecklistName$(this.projectId, control.value).pipe(
              map(() => null),
              catchError(error => {
                return of(this.formsService.buildValidationErrors(error, {
                  exists: ErrorCode.ProjectChecklistNameExistsError
                }, { unknown: true }));
              })
            );
          }
        }

        if ((this.mode === 'create-template' || this.mode === 'edit-template') && typeof this.licenseId === 'number') {
          // If the name hasn't changed then its valid
          if (this.mode === 'edit-template' && control.value === this.template?.Name) {
            return of(null);
          } else {
            return this.checklistTemplatesService.verifyChecklistTemplateName$(this.licenseId, control.value).pipe(
              map(() => null),
              catchError(error => {
                return of(this.formsService.buildValidationErrors(error, {
                  exists: ErrorCode.ChecklistTemplateNameExistsError
                }, { unknown: true }));
              })
            );
          }
        }

        return of(null);
      } else {
        return of(null);
      }
    };
  }
}
